/**
 * C-GTCC-VIEW-DIAG-AIA1 — typed read client for the "Diagnostics" view.
 *
 * Every path below already exists in the old console and is measured, not
 * invented (`services/api/src/modules/admin/diagnostics.routes.ts`,
 * `recent-errors.routes.ts`, `mail-health.routes.ts`). This module adds no
 * route and no write; the single TEST-marked write the view offers
 * (`POST /v1/admin/mail/test-send`) is the one that already exists and keeps
 * living in its own module (`../../mail/`), never in this read client.
 *
 * The GTCC status/freshness convention (`GtccMeasured`) does **not** apply
 * here: these are the old console's payloads and none of them carries
 * `status` or `freshnessSeconds`. The view therefore prints the age badge as
 * "Alter nicht messbar" rather than deriving one from the local clock
 * (Owner decisions P8/P9/P10) — `capturedAt` is a timestamp, not an age.
 *
 * Read-only by construction: `gtccGet` wraps `apiGet` only — no mutation
 * verb, no upstream URL, no PromQL.
 */
import { gtccGet, type GtccResult } from "./gtcc-result.js";

/* --------------------------------------------------------------- paths */

export const DIAG_SNAPSHOT_PATH = "/v1/admin/diagnostics/snapshot";
export const DIAG_LINKS_PATH = "/v1/admin/diagnostics/links";
/** The reference names the buffer depth explicitly: "200 Zeilen". */
export const DIAG_RECENT_ERRORS_LIMIT = 200;
export const DIAG_RECENT_ERRORS_PATH = `/v1/admin/recent-errors?limit=${DIAG_RECENT_ERRORS_LIMIT}`;
export const DIAG_MAIL_HEALTH_PATH = "/v1/admin/mail/health";

/**
 * Probe 1 (station "device") and probes 2/16 (station "gateway") of the S1
 * path-probe trace. Both routes are read-only and both need the selection
 * this view did not have before (`SONDEN.md`, "no_selection"): the caller
 * supplies the identifiers, this client adds no default and invents none.
 */
export function diagDeviceStatePath(tenantId: string, deviceId: string): string {
  const params = new URLSearchParams({ tenantId, deviceId });
  return `/v1/admin/gtcc/diag/device-state?${params.toString()}`;
}

export function diagGatewayViewPath(identifier: string): string {
  const params = new URLSearchParams({ identifier });
  return `/v1/admin/gtcc/diag/gateway-view?${params.toString()}`;
}

/* ---------------------------------------------------------------- types */

/** One dependency probe of `/readyz`, as the snapshot embeds it. */
export interface DiagDepProbeDto {
  readonly status: "ok" | "failed" | "skipped";
  readonly latencyMs?: number;
  readonly error?: string;
}

export interface DiagReadinessDto {
  readonly status: "ready" | "degraded" | "unready";
  readonly checkedAt: string;
  readonly checks: {
    readonly db: DiagDepProbeDto;
    readonly redis: DiagDepProbeDto;
    readonly keycloak: DiagDepProbeDto;
  };
}

export interface DiagVersionDto {
  readonly service: string;
  readonly version: string;
  readonly nodeVersion: string;
  readonly startedAt: string;
}

/**
 * The deliberately safe configuration echo. It carries booleans and a
 * provider *kind* — never a key, never a URL, never a secret.
 */
export interface DiagSafeConfigDto {
  readonly insightsEnabled: boolean;
  readonly authHardeningEnabled: boolean;
  readonly gatewayEnabled: boolean;
  readonly smtpProviderKind: string;
  readonly accessLogSample2xxPct: number;
  readonly readyzDepTimeoutMs: number;
  readonly nodeEnv: string;
  readonly oidcConfigured: boolean;
  readonly keycloakAdminConfigured: boolean;
}

export interface DiagSnapshotDto {
  readonly readyz: DiagReadinessDto;
  readonly version: DiagVersionDto;
  readonly config: DiagSafeConfigDto;
  readonly capturedAt: string;
}

/** A static operator reference (Grafana, runbook, /metrics, access log). */
export interface DiagLinkDto {
  readonly key: string;
  readonly label: string;
  readonly path: string;
  readonly note?: string;
}

export interface DiagLinksDto {
  readonly grafanaDashboard: DiagLinkDto;
  readonly runbookReadyz: DiagLinkDto;
  readonly metricsEndpoint: DiagLinkDto;
  readonly accessLogGuidance: DiagLinkDto;
}

export const DIAG_LINK_ORDER = [
  "grafanaDashboard",
  "runbookReadyz",
  "metricsEndpoint",
  "accessLogGuidance",
] as const satisfies ReadonlyArray<keyof DiagLinksDto>;

export interface DiagRecentErrorDto {
  readonly time: string;
  readonly level: number;
  readonly msg: string;
  readonly errorCode: string | null;
  readonly requestId: string | null;
  readonly route: string | null;
}

export interface DiagRecentErrorsDto {
  readonly items: ReadonlyArray<DiagRecentErrorDto>;
}

/**
 * Mail health. The view reads exactly one field of it — `testSendEnabled` —
 * because the "Zustell-Probe (TEST-markiert)" row must not offer a send the
 * environment has switched off. `provider` labels the transport.
 */
/**
 * The shape `services/api/src/modules/admin/mail-health.routes.ts:114` really
 * returns: the readiness flags live one level down, under `readiness`. Only
 * the one flag this view asks about is typed here — "Zustell-Probe möglich?";
 * the mail view owns the rest.
 */
export interface DiagMailHealthDto {
  readonly provider?: string;
  readonly readiness?: {
    readonly testSendEnabled?: boolean;
  };
}

/**
 * Probe 1 — station "device". Mirrors
 * `services/api/src/modules/gtcc/diag/dto.ts::diagDeviceStateResponseSchema`.
 * `outcome` is the payload's own limit (capped list, unknown device); the
 * view renders it verbatim instead of guessing an absence (Owner P8/P9/P10).
 */
export type DiagDeviceStateOutcome = "found" | "not_found" | "not_in_capped_list";

export interface DiagDeviceStateDeviceDto {
  readonly id: string;
  readonly identifier: string;
  readonly identifierType: "imei" | "uuid" | "unknown";
  readonly deviceType: string;
  readonly status: string;
  readonly vehicleId: string | null;
  readonly lastSeenAt: string | null;
  readonly lastPacketAt: string | null;
  readonly approvedAt: string | null;
  readonly archivedAt: string | null;
}

export interface DiagDeviceStateDto {
  readonly measuredAt: string;
  readonly tenantId: string;
  readonly deviceId: string;
  readonly outcome: DiagDeviceStateOutcome;
  readonly source: "devices.last_seen_at";
  readonly device: DiagDeviceStateDeviceDto | null;
  readonly list: { readonly total: number; readonly capped: boolean };
}

/**
 * Probes 2 and 16 — station "gateway". Mirrors
 * `services/api/src/modules/gtcc/diag/dto.ts::diagGatewayViewResponseSchema`.
 */
export type DiagGatewayLookupOutcome = "found" | "not_found" | "not_in_capped_list";

export interface DiagGatewayViewDto {
  readonly measuredAt: string;
  readonly identifier: string;
  readonly ingest: {
    readonly source: "ingest-observability (prozesslokal)";
    readonly outcome: DiagGatewayLookupOutcome;
    readonly enabled: boolean;
    readonly generatedAt: string;
    readonly dataSource: "memory" | "database" | "combined";
    readonly limit: {
      readonly scope: "process_local";
      readonly inMemoryWindowHours: number;
      readonly recentEventsCap: number;
      readonly devicesRead: number;
    };
    readonly device: {
      readonly identifier: string;
      readonly identifierType: "imei" | "uuid" | "unknown";
      readonly accepted: number;
      readonly rateLimited: number;
      readonly lastAccepted: string | null;
      readonly lastLimited: string | null;
      readonly lastReason: string | null;
    } | null;
  };
  readonly rejections: {
    readonly source: "device_rejection_log";
    readonly outcome: DiagGatewayLookupOutcome;
    readonly since: string;
    readonly limit: { readonly windowDays: number; readonly rowsRead: number };
    readonly entry: {
      readonly tenantId: string | null;
      readonly reason: string;
      readonly firstSeenAt: string;
      readonly lastSeenAt: string;
      readonly attemptCount: number;
    } | null;
  };
}

/* -------------------------------------------------------------- fetchers */

export function fetchDiagSnapshot(signal?: AbortSignal): Promise<GtccResult<DiagSnapshotDto>> {
  return gtccGet<DiagSnapshotDto>(DIAG_SNAPSHOT_PATH, signal);
}

export function fetchDiagLinks(signal?: AbortSignal): Promise<GtccResult<DiagLinksDto>> {
  return gtccGet<DiagLinksDto>(DIAG_LINKS_PATH, signal);
}

export function fetchDiagRecentErrors(
  signal?: AbortSignal,
): Promise<GtccResult<DiagRecentErrorsDto>> {
  return gtccGet<DiagRecentErrorsDto>(DIAG_RECENT_ERRORS_PATH, signal);
}

export function fetchDiagMailHealth(signal?: AbortSignal): Promise<GtccResult<DiagMailHealthDto>> {
  return gtccGet<DiagMailHealthDto>(DIAG_MAIL_HEALTH_PATH, signal);
}

export function fetchDiagDeviceState(
  tenantId: string,
  deviceId: string,
  signal?: AbortSignal,
): Promise<GtccResult<DiagDeviceStateDto>> {
  return gtccGet<DiagDeviceStateDto>(diagDeviceStatePath(tenantId, deviceId), signal);
}

export function fetchDiagGatewayView(
  identifier: string,
  signal?: AbortSignal,
): Promise<GtccResult<DiagGatewayViewDto>> {
  return gtccGet<DiagGatewayViewDto>(diagGatewayViewPath(identifier), signal);
}

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * Each key is an own exported array literal so the static key-consistency
 * guard (A-GTCC-QUERY-KEY-CONSISTENCY1) can follow `queryKey: DIAG_…_KEY`
 * to its literal; a member access would stay unreadable for it.
 *
 * The keys keep the segments the predecessor `GtccDiagView.tsx` already used
 * (`snapshot`, `recent-errors`, `links`), so the view boundary and the
 * registry prefix `["gtcc", "diag"]` are unchanged by this ticket.
 */
export const DIAG_SNAPSHOT_KEY = ["gtcc", "diag", "snapshot"] as const;
export const DIAG_LINKS_KEY = ["gtcc", "diag", "links"] as const;
export const DIAG_RECENT_ERRORS_KEY = ["gtcc", "diag", "recent-errors"] as const;
export const DIAG_MAIL_HEALTH_KEY = ["gtcc", "diag", "mail-health"] as const;
/**
 * Base prefixes of the two path-probe fetches, for reference only: a
 * tenant/device id is written out as a literal array at the `useQuery` call
 * site (`["gtcc", "diag", "device-state", tenantId, deviceId]`), same
 * convention as `GtccTenantsView.tsx` — the static key-consistency guard
 * (A-GTCC-QUERY-KEY-CONSISTENCY1) reads the call site itself, and a spread
 * across this module border would read back as an unreadable key.
 */
export const DIAG_DEVICE_STATE_KEY = ["gtcc", "diag", "device-state"] as const;
export const DIAG_GATEWAY_VIEW_KEY = ["gtcc", "diag", "gateway-view"] as const;

export const DIAG_QUERY_KEYS = {
  snapshot: DIAG_SNAPSHOT_KEY,
  links: DIAG_LINKS_KEY,
  recentErrors: DIAG_RECENT_ERRORS_KEY,
  mailHealth: DIAG_MAIL_HEALTH_KEY,
} as const;

export type { GtccResult };
